import { loadGoogleMapsAPI } from "@/lib/utils/loadGoogleMaps"

export interface PlacePrediction {
  description: string
  place_id: string
  structured_formatting: {
    main_text: string
    secondary_text: string
  }
}

export interface PlaceAutocompleteService {
  getPlacePredictions: (
    request: { input: string; types: string[] },
    callback: (predictions: PlacePrediction[] | null, status: string) => void,
  ) => void
}

export type AutocompleteServiceWindow = typeof window & {
  google?: {
    maps?: { places?: { AutocompleteService?: new () => PlaceAutocompleteService } }
  }
}

export function createAutocompleteService(serviceRef: {
  current: PlaceAutocompleteService | null
}): void {
  loadGoogleMapsAPI()
    .then(() => {
      const places = (window as unknown as AutocompleteServiceWindow)?.google?.maps?.places
      if (places?.AutocompleteService) {
        serviceRef.current = new places.AutocompleteService()
      }
    })
    .catch(() => {})
}

export function fetchPlacePredictions(
  service: PlaceAutocompleteService | null,
  input: string,
  onResults: (predictions: PlacePrediction[]) => void,
): void {
  if (!input.trim()) {
    onResults([])
    return
  }
  if (!service) return
  const types = [["(cities)"], ["(regions)"], ["geocode"]]
  let completed = 0
  const allPredictions: PlacePrediction[] = []
  const seenIds = new Set<string>()
  const finalize = () => {
    completed++
    if (completed === types.length) {
      onResults(allPredictions.slice(0, 13))
    }
  }
  types.forEach(typeList => {
    service.getPlacePredictions({ input, types: typeList }, (predictions, status) => {
      if (status === "OK" && predictions) {
        for (const p of predictions) {
          if (!seenIds.has(p.place_id)) {
            seenIds.add(p.place_id)
            allPredictions.push(p)
          }
        }
      }
      finalize()
    })
  })
}

export function parsePredictionLocation(prediction: PlacePrediction): {
  city: string
  state?: string
  country?: string
} {
  const mainText = prediction.structured_formatting?.main_text ?? prediction.description
  const secondary = prediction.structured_formatting?.secondary_text ?? ""
  const secondaryParts = secondary.split(",").map(s => s.trim())
  return {
    city: mainText,
    state: secondaryParts[0] || undefined,
    country: secondaryParts[1] || undefined,
  }
}
